import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';
import { LocalizationTree } from '../models/localization.model';

/**
 * The documents the consent text on the order screen points at. The key is also the placeholder
 * name in the text — `{egroup_agb}Allgemeine Geschäftsbedingungen{/egroup_agb}` — so a reader of either side can
 * find the other.
 */
export type LegalLinkKey =
	| 'egroup_agb'
	| 'egroup_homepage'
	| 'egroup_service_description'
	| 'egroup_privacy'
	| 'user_rights';

export type LegalLinks = Partial<Record<LegalLinkKey, string>>;

/** Local key under each category. The same URL is stored for both; see the service comment. */
const ENVIRONMENT_LOCAL_KEY = 'live';

/**
 * The login, `check_token` and the order all have a ceiling; this request ran without one (PR
 * review, MA2). It sits on the path between the sign-in and the next screen, so an endpoint that
 * hangs would leave the customer waiting on a fetch whose only loss is a link. Short on purpose:
 * the answer is a handful of URLs, and giving up costs nothing but those links.
 */
export const LEGAL_LINKS_TIMEOUT_MS = 8_000;

/**
 * PS-6495 — the legal URLs for the customer's brand, from the localization API.
 *
 * **Why not constants in the bundle.** They were, until 2026-10-09. The documents are the
 * contracting party's, and the contracting party follows the brand's country — a Swiss brand's
 * terms are not the German ones translated, they are different documents. Holding them in code
 * would have meant a release for every country, and a wrong document shown in the meantime.
 *
 * **Why `globalKey=url` and language 1.** The house convention for non-localized, brand-specific
 * URLs (Torsten, 2026-10-09): one row per brand, always `TEX_SPRACHE_ID = 1`, because the language
 * lives in the CONTENT of the linked page rather than in the row. The brand picks the country, the
 * country brings its language.
 *
 * **Why `fallback=false`.** With the fallback on, a brand without rows would silently be served the
 * default brand's URLs — a Swiss customer linked to German terms, with nothing anywhere going red.
 * Off, a missing row is a missing row: the term renders as plain text, which is visible.
 *
 * **Why after the sign-in.** The endpoint needs a bearer token and the brand id, and both arrive
 * together: the brand comes from `check_token`, which this flow already calls to resolve the phone
 * service. The links are only shown on the order screen, which is three steps later.
 */
@Injectable({
	providedIn: 'root',
})
export class LegalLinksService {
	private readonly http = inject(HttpClient);

	private readonly links = signal<LegalLinks>({});

	/** What has been resolved so far. Empty until `load` has run, and after a failure. */
	readonly current = this.links.asReadonly();

	/**
	 * Fetch the brand's URLs. Never throws and never reports: a failure here costs the links, and
	 * the order screen renders the document names as plain text rather than refusing to open. The
	 * consent TEXT is what gates the order — see the guided setup — not the links on it.
	 */
	async load(brandId: number | null): Promise<void> {
		if (brandId === null) {
			// No brand, nothing to look up. Not an error: `check_token` may answer without one, and
			// guessing a brand on a contract screen is worse than showing no link.
			this.links.set({});
			return;
		}

		const params = new HttpParams()
			.set('language', '1')
			.set('brandId', String(brandId))
			.set('globalKey', 'url')
			.set('fallback', 'false');

		try {
			const tree = await firstValueFrom(
				this.http
					.get<LocalizationTree>(`${environment.baseUrl}/localization/v2/i18n`, { params })
					.pipe(timeout(LEGAL_LINKS_TIMEOUT_MS)),
			);
			this.links.set(readUrls(tree));
		} catch {
			this.links.set({});
		}
	}

	/** Takes the brand's URLs from an already fetched tree (the link offer has no Bearer for `load()`). */
	setFromTree(tree: LocalizationTree | null | undefined): void {
		this.links.set(readUrls(tree));
	}
}

const LINK_KEYS: readonly LegalLinkKey[] = [
	'egroup_agb',
	'egroup_homepage',
	'egroup_service_description',
	'egroup_privacy',
	'user_rights',
];

/**
 * One category per document, the URL under a local key — the shape the house already uses for
 * `external_vpbx`. `test` and `live` hold the same value today; this reads `live` and would read
 * the other only if the two ever diverge, which is a deploy-time concern and not this app's.
 */
function readUrls(tree: LocalizationTree | null | undefined): LegalLinks {
	const categories = tree?.['url'];
	if (!categories) {
		return {};
	}

	const links: LegalLinks = {};
	for (const key of LINK_KEYS) {
		const value = categories[key]?.[ENVIRONMENT_LOCAL_KEY]?.translation?.trim();
		// Only an http(s) URL is accepted. A row holding something else is a data error, and putting
		// it into an href on a contract screen would be worse than showing no link at all.
		if (value && /^https?:\/\//i.test(value)) {
			links[key] = value;
		}
	}
	return links;
}
